export type OneTrustCookieDefinition = {
  Name?: string;
  Host?: string;
};

export type OneTrustGroupDefinition = {
  CustomGroupId?: string;
  GroupId?: string;
  OptanonGroupId?: string;
  Cookies?: OneTrustCookieDefinition[];
  FirstPartyCookies?: OneTrustCookieDefinition[];
  Hosts?: Array<{ Cookies?: OneTrustCookieDefinition[] }>;
};

export type OneTrustDomainData = {
  Groups?: OneTrustGroupDefinition[];
};

export type OneTrustWindow = Window & {
  OneTrust?: {
    GetDomainData?: () => OneTrustDomainData;
  };
  OnetrustActiveGroups?: string;
  OptanonWrapper?: () => void;
};

const EXPIRED_COOKIE_DATE = "Thu, 01 Jan 1970 00:00:00 GMT";

export function getDomainCandidates(hostname: string): string[] {
  const hostnameParts = hostname.split(".").filter(Boolean);
  const domainCandidates = new Set<string>();

  // Host-only cookie deletion (no Domain attribute)
  domainCandidates.add("");

  // Explicit host domain deletion
  domainCandidates.add(hostname);
  domainCandidates.add(`.${hostname}`);

  for (let index = 1; index < hostnameParts.length - 1; index += 1) {
    const parentDomain = hostnameParts.slice(index).join(".");
    domainCandidates.add(parentDomain);
    domainCandidates.add(`.${parentDomain}`);
  }

  return [...domainCandidates];
}

export function getPathCandidates(pathname: string): string[] {
  const pathCandidates = new Set<string>(["/"]);
  const pathSegments = pathname.split("/").filter(Boolean);

  let currentPath = "";
  for (const pathSegment of pathSegments) {
    currentPath += `/${pathSegment}`;
    pathCandidates.add(currentPath);
    pathCandidates.add(`${currentPath}/`);
  }

  return [...pathCandidates];
}

export function getVisibleCookieNames(): Set<string> {
  return new Set(
    document.cookie
      .split(";")
      .map((entry) => entry.trim().split("=")[0])
      .filter(Boolean)
  );
}

export function isHostFirstParty(host: string): boolean {
  const normalizedHost = host.trim().replace(/^\./, "").toLowerCase();
  if (!normalizedHost) {
    return true;
  }

  const currentHost = window.location.hostname.toLowerCase();
  return (
    currentHost === normalizedHost || currentHost.endsWith(`.${normalizedHost}`)
  );
}

export function deleteCookie(
  cookieName: string,
  domainCandidates: string[],
  pathCandidates: string[],
  host?: string
): void {
  const normalizedCookieName = cookieName.trim();
  if (!normalizedCookieName) {
    return;
  }

  const domainsToTry = new Set<string>(domainCandidates);
  const normalizedHost = host?.trim().replace(/^\./, "");
  if (normalizedHost) {
    domainsToTry.add(normalizedHost);
    domainsToTry.add(`.${normalizedHost}`);
  }

  for (const domain of domainsToTry) {
    for (const path of pathCandidates) {
      const domainPart = domain ? `; Domain=${domain}` : "";
      document.cookie = `${normalizedCookieName}=; Expires=${EXPIRED_COOKIE_DATE}; Max-Age=0; Path=${path}${domainPart}; SameSite=Lax`;
    }
  }
}

export function getGroupId(group: OneTrustGroupDefinition): string | undefined {
  return group.CustomGroupId ?? group.GroupId ?? group.OptanonGroupId;
}

export function getActiveGroupIds(oneTrustWindow: OneTrustWindow): Set<string> {
  return new Set(
    (oneTrustWindow.OnetrustActiveGroups ?? "")
      .split(",")
      .map((groupId) => groupId.trim())
      .filter(Boolean)
  );
}

export function getCookiesForGroup(
  group: OneTrustGroupDefinition
): OneTrustCookieDefinition[] {
  const hostCookies = (group.Hosts ?? []).flatMap(
    (hostEntry) => hostEntry.Cookies ?? []
  );
  return [
    ...(group.Cookies ?? []),
    ...(group.FirstPartyCookies ?? []),
    ...hostCookies,
  ];
}
